import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getCatalogueItems } from '../services/api';
import { ChevronLeft, ChevronRight, Package } from 'lucide-react';
import { AutoTranslate } from '../hooks/useAutoTranslate';

const CatalogueSlider = () => {
  const { language, getLocalizedPath } = useLanguage();
  const [items, setItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedItems = async () => {
      try {
        const data = await getCatalogueItems({ featured_only: true, active_only: true });
        setItems(data);
      } catch (error) {
        console.error('Error fetching featured items:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFeaturedItems();
  }, []);

  // Auto-slide every 5 seconds
  useEffect(() => {
    if (items.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % items.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [items.length]);

  const goToSlide = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentIndex(prev => (prev - 1 + items.length) % items.length);
  }, [items.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % items.length);
  }, [items.length]);

  // Don't render if no featured items
  if (loading || items.length === 0) {
    return null;
  }

  const currentItem = items[currentIndex];

  return (
    <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl overflow-hidden shadow-xl card-hover" data-testid="catalogue-slider">
      <div className="flex flex-col lg:flex-row">
        {/* Image Section - 2/5 (wider + taller to avoid upscaling small source images) */}
        <div className="lg:w-2/5 relative h-56 lg:h-80 bg-white">
          {currentItem.images && currentItem.images.length > 0 ? (
            <img 
              src={currentItem.images[0]} 
              alt={currentItem.name?.[language] || currentItem.name?.sv}
              className="w-full h-full object-contain p-4"
              style={{ imageRendering: 'auto' }}
              loading="eager"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <Package className="w-16 h-16 text-gray-300" />
            </div>
          )}
          
          {/* Navigation Arrows */}
          {items.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow-md transition-all"
                aria-label="Previous item"
              >
                <ChevronLeft className="w-5 h-5 text-gray-700" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow-md transition-all"
                aria-label="Next item"
              >
                <ChevronRight className="w-5 h-5 text-gray-700" />
              </button>
            </>
          )}
        </div>

        {/* Content Section - 3/5 */}
        <div className="lg:w-3/5 p-6 flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-xs font-mono text-amber-600 bg-amber-100 px-2 py-0.5 rounded">
              {currentItem.code}
            </span>
            {currentItem.price && (
              <span
                className="text-sm font-bold text-amber-700"
                data-testid="catalogue-min-donation"
              >
                <AutoTranslate>Minsta gåva:</AutoTranslate> {currentItem.price} kr
              </span>
            )}
          </div>
          
          <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">
            {currentItem.name?.[language] || currentItem.name?.sv || 'Namnlös'}
          </h3>
          
          {currentItem.description && (
            <p className="text-gray-600 text-sm line-clamp-2 mb-4">
              {currentItem.description?.[language] || currentItem.description?.sv}
            </p>
          )}

          <div className="flex items-center justify-between">
            <Link 
              to={getLocalizedPath('/forsamling/katalog')}
              className="inline-flex items-center text-amber-700 hover:text-amber-800 font-medium text-sm"
            >
              <AutoTranslate>Se hela katalogen</AutoTranslate>
              <ChevronRight className="w-4 h-4 ml-1" />
            </Link>

            {/* Dots indicator */}
            {items.length > 1 && (
              <div className="flex items-center gap-1.5">
                {items.map((item, idx) => (
                  <button
                    key={item.id || `slide-${idx}`}
                    onClick={() => goToSlide(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === currentIndex 
                        ? 'bg-amber-600 w-4' 
                        : 'bg-amber-300 hover:bg-amber-400'
                    }`}
                    aria-label={`Go to item ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CatalogueSlider;
